import { getBizChatWindowKey } from 'owa-copilot-chat-drawer-store';
import { getMessageStateKey } from './getMessageStateKey';
import { isBizchatContextDisabled } from './isBizchatContextDisabled';
import { updateBizchatLoadedMapMutator } from 'owa-sidepane-bizchat-store';
import { getMailboxInfoByAccountKey } from 'owa-account-source-list-store';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import isPopout from 'owa-popout-v2/lib/utils/isPopout';
import type { CallerInfo, IContextualChat } from 'owa-bizchat-types/lib/types';
import type { IPromptInputData } from 'owa-bizchat-types/lib/IPromptInputData';
import type { BizChatCompositeKey, WindowKey } from 'owa-bizchat-types';
import { getAccountKeyForMailboxInfo, type AccountKey, type MailboxInfo } from 'owa-client-types';
import { setComposeAgentModeActive } from './agentModeState';

type WindowMessageState = {
    handler?: (message: unknown) => void;
    chatContext?: IContextualChat;
    // Request awaiting delivery by message or startup data (subPageId).
    promptInputData?: IPromptInputData;
    focusInputWhenReady?: boolean;
    focusInputAfterLoading?: boolean;
    isVisible?: boolean;
    isLoading?: boolean;
    // Startup request retained across remounts, not sent by message.
    // Cleared on chat readiness, drawer close, or a new request.
    retainedStartupPromptInputData?: IPromptInputData;
    callerInfo?: CallerInfo;
    accountKey: AccountKey;
    windowKey: WindowKey;
};

/**
 * Map storing message handler state for each <window, account> pair.
 */
const messageStateMap: Map<BizChatCompositeKey, WindowMessageState> = new Map();

export function resetPostMessageHandler(
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): void {
    const state = getState(mailboxInfo, targetWindow, isImmersiveBizchat);
    if (state) {
        state.focusInputWhenReady = false;
        state.focusInputAfterLoading = false;
        state.isVisible = false;
        if (
            isPopout(targetWindow) ||
            !isGlobalFeatureEnabled('mon-copilot-bizchat-context-usecache-init')
        ) {
            // Destroy state for popouts as well as caching flag off
            destroyState(mailboxInfo, targetWindow, isImmersiveBizchat);
        } else {
            // Keep chatContext for caching and any unsent prompt, clear others
            state.handler = undefined;
            state.callerInfo = undefined;
        }
    }
    setComposeAgentModeActive(targetWindow, false);

    updateBizchatLoadedMapMutator(
        false /* isLoaded */,
        mailboxInfo,
        getBizChatWindowKey(targetWindow, isImmersiveBizchat)
    );
}

/**
 * Surgically clears only the registered post-message handler closure for a frame, without the
 * heavier teardown of `resetPostMessageHandler` (no compose-agent reset, no loaded-map update, no
 * cached chatContext/prompt destruction).
 *
 * Used when a BizChat iframe was unmounted while another surface (e.g. the Tasks Hub) was shown:
 * the stale handler still points at the now-dead frame. Clearing it forces the next
 * `openBizChatSidePane` onto the cold path (stash promptInputData + let the freshly-remounted
 * iframe pick up the conversationId) instead of posting into the dying frame on the hot path.
 */
export function invalidatePostMessageHandler(
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): void {
    const state = getState(mailboxInfo, targetWindow, isImmersiveBizchat);
    if (state) {
        state.handler = undefined;
    }
}

export function resetPostMessageHandlerForMainWindow(): void {
    for (const state of messageStateMap.values()) {
        // TODO: we need to separate owa-bizchat-shared to separate packages
        // so this main window check isn't duplicated
        if (state.windowKey === 'SidePane' || state.windowKey === 'Immersive') {
            const mailboxInfo = getMailboxInfoByAccountKey(state.accountKey);
            if (mailboxInfo) {
                resetPostMessageHandler(mailboxInfo, window);
            }
        }
    }
}

export function getIsPostMessageToFrameReady(
    mailboxInfo: MailboxInfo,
    targetWindow: Window
): boolean {
    return getState(mailboxInfo, targetWindow)?.handler !== undefined;
}

export function initializePostMessageToFrameCallback(
    postMessageToFrameCallback: (message: unknown) => void,
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): void {
    const currentState = getOrCreateState(mailboxInfo, targetWindow, isImmersiveBizchat);
    // The launch context has reached the frame; do not replay it on a later mount.
    currentState.retainedStartupPromptInputData = undefined;
    if (currentState.handler !== postMessageToFrameCallback) {
        currentState.handler = postMessageToFrameCallback;
    }
    // We need to set any pending query annotations for Sydney if Chat wasn't ready yet
    if (currentState.chatContext !== undefined && !isBizchatContextDisabled()) {
        postContextualChatChangedMessage(currentState.chatContext, mailboxInfo, targetWindow);
    }

    if (currentState.callerInfo) {
        postUpdateCallerInfoMessage(mailboxInfo, targetWindow);
    }
    focusPendingPromptInput(currentState);
}

export function setPostMessageFrameVisibility(
    isVisible: boolean,
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isLoading = false
): void {
    const state = getOrCreateState(mailboxInfo, targetWindow);
    if (!isVisible && state.isVisible) {
        state.focusInputWhenReady = false;
        state.focusInputAfterLoading = false;
    }
    if (state.isLoading && !isLoading && state.focusInputAfterLoading) {
        state.focusInputWhenReady = true;
        state.focusInputAfterLoading = false;
    }
    state.isVisible = isVisible;
    state.isLoading = isLoading;
    focusPendingPromptInput(state);
}

export function clearPostMessageFrameVisibility(
    accountKey: AccountKey,
    windowKey: WindowKey
): void {
    const state = messageStateMap.get(`${accountKey}_${windowKey}`);
    if (state) {
        state.isVisible = false;
        state.focusInputWhenReady = false;
        state.focusInputAfterLoading = false;
    }
}

function focusPendingPromptInput(state: WindowMessageState): void {
    if (state.focusInputWhenReady && state.isVisible && state.handler && !state.promptInputData) {
        state.focusInputWhenReady = false;
        // Early SSR ChatReady can arrive before the input mounts; retry once when host loading ends.
        state.focusInputAfterLoading = !!state.isLoading;
        // Native activation focuses the composer without touching the iframe DOM.
        state.handler({ func: 'onAppForeground', args: [] });
    }
}

export function postUpdatePromptInputDataMessage(
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): void {
    const currentState = getState(mailboxInfo, targetWindow, isImmersiveBizchat);
    const message = {
        func: 'updatePromptInputData',
        args: [currentState?.promptInputData],
    };
    if (currentState) {
        currentState.handler?.(message);
        currentState.promptInputData = undefined; // Clear the prompt input data after sending it
        focusPendingPromptInput(currentState);
    }
}

export function setPendingContext(
    context: IContextualChat,
    mailboxInfo: MailboxInfo,
    targetWindow: Window
): void {
    const state = getOrCreateState(mailboxInfo, targetWindow);
    state.chatContext = context;
}

export function getPendingContext(
    mailboxInfo: MailboxInfo,
    targetWindow: Window
): IContextualChat | undefined {
    return getState(mailboxInfo, targetWindow)?.chatContext;
}

/**
 * Internal function to send context changes to the BizChat frame.
 * Use setContextualChat instead for benefits like pending context handling.
 */
export function postContextualChatChangedMessage(
    contextualChat: IContextualChat,
    mailboxInfo: MailboxInfo,
    targetWindow: Window
): void {
    if (isBizchatContextDisabled()) {
        return;
    }
    const message = {
        func: 'contextualChatChanged',
        args: [contextualChat],
    };

    const currentState = getState(mailboxInfo, targetWindow);
    if (currentState) {
        currentState.handler?.(message);
        if (!isGlobalFeatureEnabled('mon-copilot-bizchat-context-usecache-init')) {
            currentState.chatContext = undefined; // Clear the chat context after sending it
        }
    }
    return;
}

export function setPromptInputData(
    newPromptInputData: IPromptInputData | undefined,
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): void {
    const state = getOrCreateState(mailboxInfo, targetWindow, isImmersiveBizchat);
    state.promptInputData = newPromptInputData;
    state.retainedStartupPromptInputData = undefined;
    // Clearing a draft after serializing it into subPageId must retain its focus request.
    if (newPromptInputData !== undefined) {
        state.focusInputWhenReady = newPromptInputData.focusOnLoadTarget === 'Compose';
        state.focusInputAfterLoading = false;
    }
}

export function getPromptInputData(
    mailboxInfo: MailboxInfo,
    targetWindow: Window
): IPromptInputData | undefined {
    const state = getState(mailboxInfo, targetWindow);
    return state?.promptInputData ?? state?.retainedStartupPromptInputData;
}

export function retainPromptInputDataForInitialization(
    promptInputData: IPromptInputData,
    mailboxInfo: MailboxInfo,
    targetWindow: Window
): void {
    const state = getState(mailboxInfo, targetWindow);
    if (state?.promptInputData === promptInputData) {
        // Rendering subPageId is not delivery: the host can remount before chatReady.
        state.retainedStartupPromptInputData = promptInputData;
        state.promptInputData = undefined;
    }
}

export function clearInitialPromptInputDataForWindow(windowKey: WindowKey): void {
    for (const state of messageStateMap.values()) {
        if (state.windowKey === windowKey) {
            state.retainedStartupPromptInputData = undefined;
        }
    }
}

export function setCallerInfo(
    callerInfo: CallerInfo,
    mailboxInfo: MailboxInfo,
    targetWindow: Window
): void {
    if (isGlobalFeatureEnabled('cal-teams-copilot-hostname')) {
        setCallerInfoState(callerInfo, mailboxInfo, targetWindow);
        if (getIsPostMessageToFrameReady(mailboxInfo, targetWindow)) {
            postUpdateCallerInfoMessage(mailboxInfo, targetWindow);
        }
    }
}

function postUpdateCallerInfoMessage(mailboxInfo: MailboxInfo, targetWindow: Window): void {
    const { callerInfo, handler } = getState(mailboxInfo, targetWindow) ?? {};
    if (!callerInfo || !handler) {
        return;
    }

    handler({
        func: 'updateCallerInfo',
        args: [callerInfo],
    });

    // Clear the callerInfo after sending it
    setCallerInfoState(undefined, mailboxInfo, targetWindow);
}

function setCallerInfoState(
    callerInfo: CallerInfo | undefined,
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): void {
    const state = getOrCreateState(mailboxInfo, targetWindow, isImmersiveBizchat);
    state.callerInfo = callerInfo;
}

function getOrCreateState(
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): WindowMessageState {
    const key = getMessageStateKey(mailboxInfo, targetWindow, isImmersiveBizchat);
    let state = messageStateMap.get(key);
    if (!state) {
        state = {
            accountKey: getAccountKeyForMailboxInfo(mailboxInfo),
            windowKey: getBizChatWindowKey(targetWindow, isImmersiveBizchat),
        };
        messageStateMap.set(key, state);
    }
    return state;
}

function getState(
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): WindowMessageState | undefined {
    const key = getMessageStateKey(mailboxInfo, targetWindow, isImmersiveBizchat);
    return messageStateMap.get(key);
}

function destroyState(
    mailboxInfo: MailboxInfo,
    targetWindow: Window,
    isImmersiveBizchat?: boolean
): boolean {
    const key = getMessageStateKey(mailboxInfo, targetWindow, isImmersiveBizchat);
    return messageStateMap.delete(key);
}
